import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import { acesso } from '@/shared/api/resources';
import { ErrorCode } from '@/shared/api/types';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';

import { fieldErrorOf } from '../shared/fieldError';
import { POLITICA_DE_SENHA } from './senha';

/**
 * Troca da propria senha.
 *
 * O servidor confere a senha atual com as protecoes do acesso e, ao trocar, encerra
 * todas as sessoes da pessoa, inclusive esta. A tela avisa antes e leva ao acesso
 * depois, em vez de deixar a proxima acao falhar sem explicacao.
 */
export function ChangePasswordModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<ApiError | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  function fechar() {
    setAtual('');
    setNova('');
    setConfirmacao('');
    setErro(null);
    setMensagem(null);
    onClose();
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    void trocar();
  }

  async function trocar() {
    setErro(null);
    setMensagem(null);
    if (nova !== confirmacao) {
      setMensagem('As duas senhas novas precisam ser iguais.');
      return;
    }
    setEnviando(true);
    try {
      await acesso.changePassword(atual, nova);
    } catch (causa) {
      if (causa instanceof ApiError && causa.code === ErrorCode.ValidationFailed) {
        setErro(causa);
      } else if (causa instanceof ApiError && causa.code === ErrorCode.RateLimited) {
        setMensagem('Muitas tentativas em pouco tempo. Aguarde um instante.');
      } else {
        setMensagem('Nao foi possivel trocar a senha. Tente novamente.');
      }
      setEnviando(false);
      return;
    }

    // A troca encerrou a sessao no servidor. O encerramento local acontece mesmo
    // que a chamada de saida falhe, e a falha aqui e esperada.
    try {
      await signOut();
    } catch {
      // A sessao ja tinha caido no servidor.
    }
    void navigate('/entrar', {
      replace: true,
      state: {
        aviso:
          'Senha alterada. Por seguranca, todas as sessoes foram encerradas. Entre com a senha nova.',
      },
    });
  }

  return (
    <Modal
      open={open}
      onClose={fechar}
      title="Alterar senha"
      description="Ao trocar a senha, todas as suas sessoes abertas sao encerradas, inclusive esta."
      footer={
        <>
          <Button variant="secondary" onClick={fechar} disabled={enviando}>
            Cancelar
          </Button>
          <Button type="submit" form="form-alterar-senha" loading={enviando}>
            Trocar senha
          </Button>
        </>
      }
    >
      <form
        id="form-alterar-senha"
        onSubmit={enviar}
        noValidate
        className="flex flex-col gap-4"
      >
        {mensagem ? <Alert tone="danger">{mensagem}</Alert> : null}
        <Field
          label="Senha atual"
          type="password"
          autoComplete="current-password"
          required
          value={atual}
          error={fieldErrorOf(erro, 'current_password')}
          onChange={(evento) => setAtual(evento.target.value)}
        />
        <Field
          label="Nova senha"
          type="password"
          autoComplete="new-password"
          required
          value={nova}
          hint={POLITICA_DE_SENHA}
          error={fieldErrorOf(erro, 'new_password')}
          onChange={(evento) => setNova(evento.target.value)}
        />
        <Field
          label="Confirmar nova senha"
          type="password"
          autoComplete="new-password"
          required
          value={confirmacao}
          onChange={(evento) => setConfirmacao(evento.target.value)}
        />
      </form>
    </Modal>
  );
}
